/**
 * Civilian Nights Out — list page ("My civilian nights").
 *
 * Auth required. Shows the events the user has RSVP'd to OR created,
 * sorted by date. Empty state with a CTA to create the first one.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO, isValid } from 'date-fns';
import Navbar from '@/components/layout/Navbar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Sparkles, Plus, Calendar as CalIcon, MapPin, Users } from 'lucide-react';
import FavouriteButton from '@/components/FavouriteButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const prettyDate = (iso) => {
  if (!iso) return '';
  try {
    const d = parseISO(iso);
    if (!isValid(d)) return iso;
    return format(d, "EEE d MMM 'at' HH:mm");
  } catch {
    return iso;
  }
};

const CivilianNightsListPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
        if (!token) {
          navigate('/login?returnTo=/civilian');
          return;
        }
        const { data } = await axios.get(`${API}/civilian-nights/mine`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setItems(data.items || []);
      } catch (err) {
        if (!cancelled) toast.error(err?.response?.data?.detail || 'Could not load your civilian nights');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="civilian-list-page">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 pt-24">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="font-heading text-3xl flex items-center gap-2">
              <Sparkles className="w-7 h-7 text-fuchsia-400" />
              Civilian Nights
            </h1>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
              Link-only invites — never public. Raves, club nights, day parties,
              anything that isn&apos;t a sporting motive. Snap a flyer, share
              the link with your crew, RSVPs land in everyone&apos;s calendar.
            </p>
          </div>
          <Button
            onClick={() => navigate('/civilian/new')}
            className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white"
            data-testid="civilian-list-new-btn"
          >
            <Plus className="w-4 h-4 mr-1" />New
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : items.length === 0 ? (
          <Card className="bg-card/60 border-border/50">
            <CardContent className="p-10 text-center space-y-3" data-testid="civilian-list-empty">
              <Sparkles className="w-10 h-10 mx-auto text-fuchsia-400" />
              <p className="font-medium">No civilian nights yet.</p>
              <p className="text-sm text-muted-foreground">
                Create one and drop the link in your group chat.
              </p>
              <Button
                onClick={() => navigate('/civilian/new')}
                className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white"
              >
                <Plus className="w-4 h-4 mr-1" />Create a Civilian Night
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {items.map((ev) => (
              <Link
                key={ev.civ_id}
                to={`/civilian/${ev.civ_id}`}
                className="block"
                data-testid={`civilian-item-${ev.civ_id}`}
              >
                <Card className="bg-card/60 border-border/50 hover:border-fuchsia-700/60 transition">
                  <CardContent className="p-4 flex gap-3 items-start">
                    {ev.image_url && (
                      <img
                        src={ev.image_url}
                        alt={ev.name}
                        className="w-20 h-20 object-cover rounded flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <h2 className="font-semibold leading-tight truncate">{ev.name}</h2>
                      <div className="mt-1 text-xs text-muted-foreground space-y-0.5">
                        <div className="flex items-center gap-1.5">
                          <CalIcon className="w-3 h-3" />
                          <span>{prettyDate(ev.date)}</span>
                        </div>
                        {ev.location?.address && (
                          <div className="flex items-center gap-1.5">
                            <MapPin className="w-3 h-3" />
                            <span className="truncate">{ev.location.address}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1.5">
                          <Users className="w-3 h-3" />
                          <span>{(ev.going || []).length} going</span>
                        </div>
                      </div>
                    </div>
                    <FavouriteButton kind="civilian" id={ev.civ_id} size="sm" />
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CivilianNightsListPage;
